import { useEffect, useMemo, useState } from 'react';
import { createStaticMapsUrl } from '@vis.gl/react-google-maps';
import getConfig from 'next/config';

import { getStaticMapUrl } from 'Services/api/vehicleDetails';
import { logger } from 'Services/logger/logger';
import { tryToLoadImage } from 'Utilities/images';

import type { Locations } from '../../LocationSection.types';
import {
	DEALERSHIP_LOCATION_ICON_URL,
	MAP_HEIGHT,
	MAX_ZOOM_LEVEL,
	VEHICLE_LOCATION_ICON_URL,
} from '../SwitchableMap.consts';
import { isMaxZoomLevel } from '../SwitchableMap.helpers';

const { publicRuntimeConfig } = getConfig();
const { GOOGLE_MAPS_ID, GOOGLE_STATIC_MAPS_API } = publicRuntimeConfig;

interface UseStaticMapUrlProps {
	distance?: number;
	isDealerMarkerVisible: boolean;
	locations: Locations;
	mapWidth: number;
	onError: () => void;
}

export const useStaticMapUrl = ({
	distance = 0,
	isDealerMarkerVisible,
	locations,
	mapWidth,
	onError,
}: UseStaticMapUrlProps) => {
	const shouldDisplayDealerMarker = isDealerMarkerVisible && locations.dealer.latitude && locations.dealer.longitude;
	const [staticMapUrl, setStaticMapUrl] = useState<string>('');

	const staticMapUrlUnsigned = useMemo(
		() =>
			createStaticMapsUrl({
				apiKey: GOOGLE_STATIC_MAPS_API,
				height: MAP_HEIGHT,
				mapId: GOOGLE_MAPS_ID,
				markers: [
					...(shouldDisplayDealerMarker
						? [
								{
									anchor: 'center',
									icon: DEALERSHIP_LOCATION_ICON_URL,
									label: 'Dealer location',
									location: { lat: locations.dealer.latitude ?? 0, lng: locations.dealer.longitude ?? 0 },
								},
						  ]
						: []),
					{
						anchor: 'center',
						icon: VEHICLE_LOCATION_ICON_URL,
						label: 'Seller location',
						location: { lat: locations.seller.latitude ?? 0, lng: locations.seller.longitude ?? 0 },
					},
				],
				scale: 2,
				width: mapWidth,
				zoom: isMaxZoomLevel(distance) ? MAX_ZOOM_LEVEL : undefined,
			}),
		[
			distance,
			locations.dealer.latitude,
			locations.dealer.longitude,
			locations.seller.latitude,
			locations.seller.longitude,
			mapWidth,
			shouldDisplayDealerMarker,
		],
	);

	useEffect(() => {
		const signMapUrl = async (url: string) => {
			try {
				const signedUrl = await getStaticMapUrl({ url });
				await tryToLoadImage(signedUrl);
				setStaticMapUrl(signedUrl);
			} catch (error) {
				onError();
				logger.error({ error: error as Error, message: 'Error fetching signed static map url', scope: 'signMapUrl' });
			}
		};
		if (mapWidth) {
			void signMapUrl(staticMapUrlUnsigned);
		}
	}, [staticMapUrlUnsigned, mapWidth]);

	return staticMapUrl;
};
